import React, { useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router';
import {
  useApi,
  Grid,
  GridColumn,
  Card,
  CardSection,
  CardHeader,
  SmallHeader,
  Form,
  Gutter,
  Layout,
  Toggle,
  Suspense,
} from '@axiom/ui';
import { CandidateSchema } from '@axiom/validation';
import { AuthConst } from '@axiom/const';

import { PreferencesNavigation } from '../PreferencesNavigation/PreferencesNavigation';
import { PreferencesContactInformation } from '../PreferencesContactInformation/PreferencesContactInformation';
import { WorkFeedAlerts } from '../WorkFeedAlerts/WorkFeedAlerts';
import { UserApi } from '../../api/user-api';
import { CandidateUtil } from '../../utils/candidate-util';
import { PreferencesMfaPhone } from '../PreferencesMfaPhone/PreferencesMfaPhone';
import { CandidateApi } from '../../api/candidate-api';
import { PreferencesCommunications } from '../PreferencesCommunications/PreferencesCommunications';

const CommunicationPreferencesAlertsSchema = CandidateSchema.pick({
  isOptInWorkFeedEmail: true,
});

export const PreferencesCommunication = () => {
  const [{ data: user }] = useApi(UserApi.readUser());
  const { candidateId: urlCandidateId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const alertRef = useRef<HTMLDivElement>(null);
  const scrollTarget = searchParams.get('scrollTo');

  const candidateId = CandidateUtil.reconcileUserCandidateId(
    user,
    urlCandidateId
  );

  const [{ data: candidate }, { data: mfa }] = useApi(
    CandidateApi.readCandidate(candidateId),
    CandidateApi.readCandidateMfaPhoneNumber(candidateId)
  );

  const { isAccessibleToWorkFeed, isOptInWorkFeedEmail } = candidate;

  const isMfaAllowed = () => {
    const restrictedCountries =
      AuthConst.IneligibleMultiFactorAuthenticationCountryCodes;

    const isAddressAllowed = !restrictedCountries.includes(
      candidate.addressCountryCode
    );

    const isPhoneAllowed =
      mfa.notificationsPhoneNumberCountry &&
      !restrictedCountries.includes(mfa.notificationsPhoneNumberCountry);

    return isAddressAllowed || isPhoneAllowed;
  };

  useEffect(() => {
    if (scrollTarget && alertRef.current) {
      // Small delay to ensure DOM is fully rendered
      setTimeout(() => {
        alertRef.current?.scrollIntoView({ behavior: 'smooth' });
        searchParams.delete('scrollTo');
        setSearchParams(searchParams, { replace: true });
      }, 100);
    }
  }, [scrollTarget]);

  return (
    <Grid name="COMMUNICATION_PREFERENCES">
      <GridColumn largeScreenWidth={4} smallScreenWidth={12}>
        <PreferencesNavigation />
        <Gutter only="smallScreen" bottom="16px" />
      </GridColumn>
      <GridColumn largeScreenWidth={8} smallScreenWidth={12}>
        <Layout direction="vertical" verticalGutter="16px">
          {isMfaAllowed() ? (
            <PreferencesMfaPhone candidate={candidate} user={user} />
          ) : null}
          <PreferencesCommunications candidate={candidate} mfa={mfa} />
          <PreferencesContactInformation candidate={candidate} />
          {isAccessibleToWorkFeed ? (
            <div ref={alertRef}>
              <Card name="ALERTS_PREFERENCES_CARD">
                <CardHeader>
                  <SmallHeader name="ALERTS_PREFERENCES_CARD_HEADER">
                    Alerts
                  </SmallHeader>
                </CardHeader>
                <CardSection divider>
                  <Form
                    name="ALERTS-FORM"
                    schema={CommunicationPreferencesAlertsSchema}
                    onSubmit={async formData => {
                      return CandidateApi.updateCandidate(
                        candidateId,
                        CandidateSchema.partial().parse(formData)
                      ).then(async () => {
                        return CandidateApi.refreshCandidate(candidateId);
                      });
                    }}
                    initialValues={{
                      isOptInWorkFeedEmail: !!isOptInWorkFeedEmail,
                    }}
                    submitOnChange
                  >
                    {() => (
                      <Toggle
                        name="isOptInWorkFeedEmail"
                        label="Alert Subscriptions"
                        description="I'd like to receive opportunity alerts via email. I'll get alerts that fit my requested hours and account for my upcoming end dates."
                      />
                    )}
                  </Form>
                </CardSection>
                <CardSection>
                  <Suspense>
                    <WorkFeedAlerts candidateId={candidateId} />
                  </Suspense>
                </CardSection>
              </Card>
            </div>
          ) : null}
        </Layout>
      </GridColumn>
    </Grid>
  );
};
